import type { ButtonSize, ButtonVariant } from "@/components/button/button/button";
import { toCmsHref } from "@/lib/cmsHelpers";

export type ButtonVisibility =
  | "hidden"
  | "all on card"
  | "first on card"
  | "all on overlay";

export interface NormalizedCardButton {
  id?: number | string;
  copy?: string;
  title?: string;
  href: string;
  target?: string;
  isBlank: boolean;
  variant: ButtonVariant;
  size: ButtonSize;
  customId: string;
}

const normalizeRelationArray = (rel: unknown): unknown[] => {
  if (!rel) return [];
  if (Array.isArray(rel)) return rel;
  const data = (rel as { data?: unknown })?.data;
  if (Array.isArray(data)) return data;
  return [];
};

const mapVariant = (raw: unknown): ButtonVariant => {
  const val = String(raw || "").toLowerCase().trim();
  if (
    val === "primary" ||
    val === "default" ||
    val === "secondary" ||
    val === "ghost" ||
    val === "link" ||
    val === "destructive"
  ) {
    return val as ButtonVariant;
  }
  return "default";
};

// CMS ships xs/sm/md/lg/xl; Button renders each as its own visual tier.
const mapSize = (raw: unknown): ButtonSize => {
  const val = String(raw || "").toLowerCase().trim();
  if (val === "xs") return "xs";
  if (val === "sm" || val === "small") return "sm";
  if (val === "lg" || val === "large") return "lg";
  if (val === "xl" || val === "xlarge" || val === "extra-large") return "xl";
  return "default";
};

const resolveHref = (rawHref: string, customId: string): string => {
  const href = (rawHref || "").trim();
  if (!href) return "";
  if (customId === "email") {
    return href.toLowerCase().startsWith("mailto:") ? href : `mailto:${href}`;
  }
  // Editors may have entered the URL without a leading slash; toCmsHref
  // leaves anchors and absolute URLs (http://, https://, mailto:, tel:,
  // sms:) alone and adds a leading slash to bare paths.
  return toCmsHref(href) || href;
};

export const normalizeCardButton = (raw: unknown): NormalizedCardButton => {
  const rawObj = (raw ?? {}) as {
    id?: number | string;
    attributes?: Record<string, unknown>;
  } & Record<string, unknown>;
  const attrs = (rawObj.attributes ?? rawObj ?? {}) as Record<string, unknown> & {
    id?: number | string;
  };
  const customId = String(attrs.button_customID ?? "").toLowerCase().trim();
  const target = String(attrs.target ?? "self").toLowerCase();
  const href = resolveHref(String(attrs.url ?? ""), customId);
  return {
    id: rawObj.id ?? attrs.id,
    copy: attrs.copy as string | undefined,
    title: attrs.title as string | undefined,
    href,
    target,
    isBlank: customId !== "email" && target === "blank",
    variant: mapVariant(attrs.type),
    size: mapSize(attrs.size),
    customId,
  };
};

export const normalizeCardButtons = (rel: unknown): NormalizedCardButton[] =>
  normalizeRelationArray(rel).map(normalizeCardButton);

export interface SplitButtonsResult {
  cardButtons: NormalizedCardButton[];
  overlayButtons: NormalizedCardButton[];
}

/**
 * Split a card's buttons into the slice rendered on the card and the slice
 * rendered in the click-triggered CardOverlay, based on the CMS visibility
 * setting. Unknown / undefined visibility falls back to "all on card" so
 * cards predating this field still render their buttons.
 */
export const splitButtons = (
  buttons: NormalizedCardButton[],
  visibility?: ButtonVisibility | string | null
): SplitButtonsResult => {
  const v = String(visibility ?? "").toLowerCase().trim();
  if (v === "hidden") {
    return { cardButtons: [], overlayButtons: [] };
  }
  if (v === "all on overlay") {
    return { cardButtons: [], overlayButtons: buttons };
  }
  if (v === "first on card") {
    return {
      cardButtons: buttons.slice(0, 1),
      overlayButtons: buttons.slice(1),
    };
  }
  // 'all on card' or anything else — show every button on the card.
  return { cardButtons: buttons, overlayButtons: [] };
};

export type { ButtonVariant, ButtonSize };
